import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Image as ImageIcon, Trash2, X, Save, ExternalLink, Activity, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const AdsSection = ({
    ads,
    showAdModal,
    setShowAdModal,
    newAd,
    setNewAd,
    addAd,
    deleteAd,
    toggleAdStatus,
    isMobile
}) => {
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="flex justify-between items-center px-2">
                <div>
                    <h3 className="text-2xl font-black text-foreground tracking-tight">Advertisement Hub</h3>
                    <p className="text-muted-foreground text-sm font-bold opacity-70">Manage banners, popups, and sidebar promos</p>
                </div>
                <Button
                    onClick={() => setShowAdModal(true)}
                    className="rounded-2xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-xs px-8 py-7 shadow-xl shadow-primary/20 hover:scale-105 transition-all gap-2"
                >
                    <Plus size={20} /> Create New Advertisement
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {ads.map((ad, i) => (
                    <motion.div
                        key={ad.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                    >
                        <Card className="rounded-[2.5rem] bg-card/40 backdrop-blur-xl border-border/40 overflow-hidden group hover:border-primary/20 transition-all flex flex-col h-full shadow-lg">
                            <div className="relative aspect-video bg-secondary/20 overflow-hidden">
                                {ad.imageUrl ? (
                                    <img src={ad.imageUrl} alt={ad.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/30">
                                        <ImageIcon size={48} />
                                    </div>
                                )}
                                <div className="absolute top-4 right-4 flex gap-2">
                                    <Badge className={`rounded-xl px-4 py-1.5 font-black text-[10px] tracking-widest uppercase backdrop-blur-md shadow-lg ${ad.isActive ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/40' : 'bg-rose-500/20 text-rose-500 border-rose-500/40'}`}>
                                        {ad.isActive ? 'ACTIVE' : 'INACTIVE'}
                                    </Badge>
                                </div>
                            </div>

                            <div className="p-8 flex flex-col flex-1">
                                <div className="mb-4">
                                    <Badge variant="outline" className="rounded-lg px-2 text-[10px] font-black uppercase tracking-widest border-border/60 text-muted-foreground">
                                        {ad.type}
                                    </Badge>
                                </div>
                                <h4 className="text-xl font-black text-foreground tracking-tight mb-2 group-hover:text-primary transition-colors">{ad.title}</h4>
                                <p className="text-muted-foreground text-xs font-bold opacity-60 line-clamp-2 mb-8 italic">
                                    {ad.targetUrl}
                                </p>

                                <div className="mt-auto flex items-center gap-4">
                                    <Button
                                        onClick={() => toggleAdStatus(ad)}
                                        variant="outline"
                                        className="flex-1 rounded-2xl h-[50px] border-border/60 font-black uppercase tracking-widest text-[10px] hover:bg-primary/5 hover:text-primary transition-all flex gap-2"
                                    >
                                        {ad.isActive ? <ToggleRight className="text-emerald-500" /> : <ToggleLeft className="text-rose-500" />}
                                        {ad.isActive ? 'Deactivate' : 'Activate'}
                                    </Button>
                                    <Button
                                        onClick={() => deleteAd(ad.id)}
                                        variant="ghost"
                                        size="icon"
                                        className="w-[50px] h-[50px] rounded-2xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all shadow-inner"
                                    >
                                        <Trash2 size={20} />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </motion.div>
                ))}

                {ads.length === 0 && (
                    <Card className="p-24 text-center bg-card/40 backdrop-blur-xl border-dashed border-2 border-border/60 rounded-[3rem] col-span-full opacity-50">
                        <ImageIcon size={64} className="mx-auto mb-6 opacity-20" />
                        <div className="text-xl font-black text-foreground tracking-tight">No advertisements found</div>
                        <p className="text-muted-foreground text-sm font-bold mt-2">Schedule your first campaign to get started.</p>
                    </Card>
                )}
            </div>

            <AnimatePresence>
                {showAdModal && (
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAdModal(false)} className="absolute inset-0 bg-background/80 backdrop-blur-xl" />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative w-full max-w-xl bg-card border border-border rounded-[3rem] shadow-2xl overflow-hidden"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="p-10 space-y-8">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-3xl font-black text-foreground tracking-tight">Create Ad</h3>
                                        <p className="text-muted-foreground text-sm font-black uppercase tracking-widest opacity-60">Push a new promotion live</p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => setShowAdModal(false)} className="rounded-full w-12 h-12 border border-border bg-secondary/20 hover:bg-secondary/40 transition-all">
                                        <X size={24} />
                                    </Button>
                                </div>

                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">Ad Title *</label>
                                            <Input
                                                placeholder="e.g. Special Offer"
                                                value={newAd.title}
                                                onChange={e => setNewAd({ ...newAd, title: e.target.value })}
                                                className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight h-[55px]"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">Ad Type</label>
                                            <select
                                                className="w-full rounded-2xl p-3 bg-secondary/30 border border-border/60 font-bold text-sm outline-none focus:ring-2 focus:ring-primary/20 appearance-none h-[55px]"
                                                value={newAd.type}
                                                onChange={e => setNewAd({ ...newAd, type: e.target.value })}
                                            >
                                                <option value="banner">Banner</option>
                                                <option value="popup">Popup</option>
                                                <option value="sidebar">Sidebar</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">Ad Image *</label>
                                        <div className="flex flex-col gap-4">
                                            {newAd.imageUrl ? (
                                                <div className="relative aspect-video rounded-2xl overflow-hidden border border-border group">
                                                    <img src={newAd.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                                                    <button
                                                        onClick={() => setNewAd({ ...newAd, imageUrl: '' })}
                                                        className="absolute top-2 right-2 p-2 bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <label className="flex flex-col items-center justify-center aspect-video rounded-2xl border-2 border-dashed border-border/60 bg-secondary/10 cursor-pointer hover:bg-secondary/20 transition-all">
                                                    <ImageIcon size={32} className="text-muted-foreground/40 mb-2" />
                                                    <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">Select Image</span>
                                                    <span className="text-[10px] text-muted-foreground/60 mt-1">Max 1MB (Optimal for Base64)</span>
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                if (file.size > 1024 * 1024) {
                                                                    alert('Image size exceeds 1MB. Please optimize for faster loading.');
                                                                    return;
                                                                }
                                                                const reader = new FileReader();
                                                                reader.onloadend = () => setNewAd({ ...newAd, imageUrl: reader.result });
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">Width (px)</label>
                                            <Input
                                                placeholder="e.g. 728"
                                                type="number"
                                                value={newAd.width || ''}
                                                onChange={e => setNewAd({ ...newAd, width: e.target.value })}
                                                className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight h-[55px]"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">Height (px)</label>
                                            <Input
                                                placeholder="e.g. 90"
                                                type="number"
                                                value={newAd.height || ''}
                                                onChange={e => setNewAd({ ...newAd, height: e.target.value })}
                                                className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight h-[55px]"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">Target URL *</label>
                                        <Input
                                            placeholder="https://example.com/landing"
                                            value={newAd.targetUrl}
                                            onChange={e => setNewAd({ ...newAd, targetUrl: e.target.value })}
                                            className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight h-[55px]"
                                        />
                                    </div>

                                    <div className="flex items-center gap-4 bg-secondary/20 p-4 rounded-2xl border border-border/40">
                                        <input
                                            type="checkbox"
                                            id="ad-active"
                                            checked={newAd.isActive}
                                            onChange={e => setNewAd({ ...newAd, isActive: e.target.checked })}
                                            className="w-5 h-5 accent-primary rounded-lg cursor-pointer"
                                        />
                                        <label htmlFor="ad-active" className="text-sm font-black text-foreground cursor-pointer uppercase tracking-widest opacity-80">Activate on launch</label>
                                    </div>

                                    <Button
                                        onClick={addAd}
                                        className="w-full py-8 rounded-[1.5rem] bg-primary text-primary-foreground font-black uppercase tracking-widest text-xs shadow-2xl shadow-primary/40 flex items-center justify-center gap-3 transition-all hover:scale-[1.02] hover:shadow-primary/50"
                                    >
                                        <Save size={20} /> Schedule Advertisement
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default AdsSection;
